/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * ---------------------------------------------------------------------------------------------
 */

import {
  Card,
  makeStyles,
  tokens,
  Image,
  Link,
} from '@fluentui/react-components';
import React from 'react';

import { Box, Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';

export interface IToolTile {
  id: string;
  title: string;
  description: string;
  icon: string;
  link: string;
  extra?: React.ReactNode;
}

const useStyle = makeStyles({
  inner: {
    position: 'relative',
    color: tokens.colorNeutralForeground1,
    minHeight: '100px',
    maxHeight: '120px',
    paddingLeft: '15px',
    alignItems: 'center',
    gap: '20px',
  },
  textWrap: {
    paddingRight: '25px',
    gap: '8px',
  },
  iconWrap: {
    width: '64px',
    marginRight: '12px',
  },
  box: {
    width: '64px',
    height: ' 64px',
    borderRadius: '8px',
  },
  icon: {
    borderRadius: '6px',
    width: '64px',
    height: '64px',
  },
  title: {
    fontWeight: 700,
    lineHeight: '20px',
    fontSize: '16px',
  },
  descr: {
    fontWeight: 400,
    lineHeight: '16px',
    paddingTop: '2px',
    fontSize: '14px',
  },
  extra: {
    position: 'absolute',
    right: '20px',
    top: '33%',
  },
  grid: {
    width: '100%',
    height: '100%',
    gap: '20px',
    gridTemplateColumns: 'none',
    '@media (min-width: 640px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px',
    },
  },
});
export const ToolTile: React.FC<IToolTile> = ({
  id,
  link,
  icon,
  title,
  description,
}) => {
  const toolTileClasses = useStyle();
  const onClick = () => {
    // TODO - handle click telemetry
    window.open(link, "_blank");
  };

  return (
    <Card id={id} appearance="filled" onClick={onClick}>
      <Flex column>
        <Flex fill className={toolTileClasses.inner}>
          <Flex column className={toolTileClasses.iconWrap}>
            <Box className={toolTileClasses.box}>
              <Image
                width="64px"
                height="64px"
                src={icon}
                className={toolTileClasses.icon}
                alt={title}
              />
            </Box>
          </Flex>
          <Flex column className={toolTileClasses.textWrap}>
            <StyledText className={toolTileClasses.title} size="medium">
              {title}
            </StyledText>
            <StyledText className={toolTileClasses.descr} size="small">
              {description}
            </StyledText>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
